import React, { useState, useRef, useEffect } from 'react';
import { AppTheme } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import './ActionMenu.css';

interface ActionMenuItem {
  label: string;
  icon: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  theme: AppTheme;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({ items, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button
        className={`action-menu-button ${theme.isDark ? 'dark' : 'light'}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <span className="action-menu-dots">⋮</span>
      </button>

      {isOpen && (
        <>
          <div className="action-menu-overlay" onClick={() => setIsOpen(false)} />
          <div className={`action-menu-modal ${theme.isDark ? 'dark' : 'light'}`}>
            <div className="action-menu-modal-header">
              <h3 className={`action-menu-modal-title ${theme.isDark ? 'dark' : 'light'}`}>
                {t('table.actions')}
              </h3>
              <button
                className={`action-menu-close ${theme.isDark ? 'dark' : 'light'}`}
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="action-menu-modal-content">
              {items.map((item, index) => (
                <button
                  key={index}
                  className={`action-menu-modal-item ${theme.isDark ? 'dark' : 'light'} ${item.variant === 'danger' ? 'danger' : ''}`}
                  onClick={() => handleItemClick(item.onClick)}
                >
                  <div className={`action-menu-modal-item-icon ${theme.isDark ? 'dark' : 'light'}`}>
                    {item.icon}
                  </div>
                  <span className="action-menu-modal-item-label">{item.label}</span>
                  <span className="action-menu-modal-item-arrow">›</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
